import React, { useEffect } from "react";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import "@fontsource/federo/400.css";
import "@fontsource/dm-sans/300.css";
import "@fontsource/dm-sans/400.css";
import "@fontsource/dm-sans/500.css";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { loadReCaptcha } from "react-recaptcha-v3";
import { AppSnackbarProvider, AuthProvider, MapboxCacheProvider, useAuth } from "@healthdesk/shared-hooks";
import { DeviceProvider } from "./context/Device";
import { ClinicContextProvider } from "./contexts/ClinicContext";
import { AppRoutes } from "./routes/AppRoutes";
import {
  prefetchClinicalReferences,
  shouldPrefetchClinicalReferences,
} from "./services/llm/clinicalReferenceService";
import { muiTheme } from "./config/theme";
import { initSentry } from "./config/sentry";
import theme from "./config/theme";
import { resumeClickAudio } from "./pages/splash/splitflapAudio";
import AppErrorBoundary from "./components/AppErrorBoundary";
import SentryContextSync from "./components/SentryContextSync";

/** Prefetch clinical references only for roles allowed to read `clinicalReferences` (see firestore.rules). */
function ClinicalRefsPrefetch({ children }) {
  const { user, userData, userLoading } = useAuth();
  useEffect(() => {
    if (!user || user.isAnonymous || userLoading) return;
    if (!shouldPrefetchClinicalReferences(userData?.role)) return;
    prefetchClinicalReferences();
  }, [user, userData?.role, userLoading]);
  return children;
}

// Initialize Sentry
initSentry();

function App() {
  useEffect(() => {
    // Skip reCAPTCHA for Kijabe pages - check BOTH path and iframe
    const isKijabeDashboard =
      window.location.pathname.includes("kijabe-dashboard");
    const isIframe = window !== window.parent;
    const isProduction = process.env.REACT_APP_ENVIRONMENT === "production";

    // Don't load reCAPTCHA if we're on kijabe-dashboard, in an iframe, or not in production
    if (!isKijabeDashboard && !isIframe && isProduction) {
      try {
        loadReCaptcha(process.env.REACT_APP_RECAPTCHA_SITE_KEY, () => {
        });
      } catch (error) {
        console.warn('⚠️ reCAPTCHA failed to load:', error);
        // Non-blocking error - app continues to work without reCAPTCHA
      }
    } else {
    }
  }, []);

  const api_regex = /^\/api\/.*/;
  if (api_regex.test(window.location.pathname)) {
    return <div />;
  }

  return (
    <AppSnackbarProvider
      maxSnack={3}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      autoHideDuration={4000}
    >
      <MuiThemeProvider theme={muiTheme}>
        <ThemeProvider theme={theme}>
          <AuthProvider onAuthenticated={resumeClickAudio}>
            <ClinicalRefsPrefetch>
              <ClinicContextProvider>
                <DeviceProvider>
                  <MapboxCacheProvider>
                    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                      <AppErrorBoundary>
                        <SentryContextSync />
                        <div className="App">
                          <AppRoutes />
                        </div>
                      </AppErrorBoundary>
                    </BrowserRouter>
                  </MapboxCacheProvider>
                </DeviceProvider>
              </ClinicContextProvider>
            </ClinicalRefsPrefetch>
          </AuthProvider>
        </ThemeProvider>
      </MuiThemeProvider>
    </AppSnackbarProvider>
  );
}

export default App;
